import React, { useState, useEffect } from "react";
import { X, Check, Ban, Loader2 } from "lucide-react";
import { DENY_REASONS, DenyReason, UnderwritingAssessment } from "@/types/underwriting";
import { ApplicationNotesDict } from "@/types/borrower";
import { flattenAndSort } from "./NotesDialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn, pluralize } from "@/lib/utils";

export interface OfferValues {
  fundingAmount: number;
  factor: number;
  installment: number;
  term: number;
  frequency: string;
  addedLeverage: number;
}

export interface ActivePosition {
  funder: string;
  amount: number;
  frequency: string;
}

/**
 * `phoneSheet`: below sm the dialog sits on the screen's bottom edge, full width, rather than
 * centred with a strip of the page under it (a chat card's confirm).
 */
const PHONE_SHEET =
  "max-sm:bottom-0 max-sm:top-auto max-sm:max-h-[90dvh] max-sm:translate-y-0 max-sm:rounded-t-xl max-sm:pb-[max(1.5rem,env(safe-area-inset-bottom))]";

// ─── AcceptDialog ─────────────────────────────────────────────────────────────

interface AcceptDialogProps {
  open: boolean;
  onClose: () => void;
  /** Pre-computed effective active positions (respects user toggles) */
  activePositions: ActivePosition[];
  ordinalPosition?: string;
  offerValues: OfferValues;
  onConfirm: (notes: string, sendContract: boolean) => void;
  submitting: boolean;
  /** If provided, pre-populates the notes field with the existing assessment's notes */
  existingAssessment?: UnderwritingAssessment | null;
  applicationNotes?: ApplicationNotesDict;
  /** What this approval will do with the default merchant onboarding flow. */
  onboardingNotice?: string | null;
  /** Render that notice as a failure instead of helper text. */
  onboardingNoticeError?: boolean;
  /** False while that notice is still loading. Defaults to ready. */
  confirmReady?: boolean;
  /**
   * When true, show Approve + Send Contract beside Approve.
   * When false, only Approve (sendContract is always false).
   */
  showSendContractOption?: boolean;
  /**
   * A note someone else proposed (a chat approval): shown read-only and confirmed as it is,
   * with `hint` (how to change it) below; `editable`, the box starts at it and the user may
   * change it. Without it the note box is the user's own.
   */
  proposedNote?: { text: string; hint?: React.ReactNode; editable?: boolean };
  /** How many older notes `applicationNotes` leaves out (a chat approval sends the newest). */
  olderNotes?: number;
  /**
   * The notice says the contract cannot go out: Approve + Send Contract is off and Approve is
   * the main button (a chat approval).
   */
  sendContractBlocked?: boolean;
  /** On a phone, sit on the bottom edge (PHONE_SHEET). */
  phoneSheet?: boolean;
}

export const AcceptDialog: React.FC<AcceptDialogProps> = ({
  open,
  onClose,
  activePositions,
  ordinalPosition,
  offerValues,
  onConfirm,
  submitting,
  existingAssessment,
  applicationNotes,
  onboardingNotice,
  onboardingNoticeError = false,
  confirmReady = true,
  showSendContractOption = false,
  proposedNote,
  olderNotes = 0,
  sendContractBlocked = false,
  phoneSheet = false,
}) => {
  const fixedNote = proposedNote && !proposedNote.editable ? proposedNote : null;
  const initialNotes = () =>
    proposedNote?.editable ? proposedNote.text : (existingAssessment?.notes ?? "");
  const [notes, setNotes] = useState(initialNotes);

  useEffect(() => {
    if (open) {
      setNotes(initialNotes());
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConfirm = (sendContract: boolean) => {
    onConfirm(fixedNote ? fixedNote.text : notes, sendContract);
  };

  const handleClose = () => {
    if (!submitting) {
      setNotes("");
      onClose();
    }
  };

  // Approve is the green button unless Approve + Send Contract can be pressed beside it.
  const approveMain = !showSendContractOption || sendContractBlocked;

  const freqLabel =
    offerValues.frequency === "weekly" ? "wk" : offerValues.frequency === "monthly" ? "mo" : "day";

  const labelClass =
    "mb-1 text-[0.7rem] font-semibold uppercase tracking-wide text-muted-foreground";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent
        className={cn(
          "flex max-h-[85vh] max-w-lg flex-col sm:max-w-lg",
          "[&>button.absolute]:hidden",
          phoneSheet && PHONE_SHEET
        )}
        onPointerDownOutside={(e) => submitting && e.preventDefault()}
      >
        <DialogHeader className="space-y-0 text-left">
          <div className="flex items-center justify-between pr-8">
            <DialogTitle className="text-xl font-bold">Confirm Approve</DialogTitle>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              onClick={handleClose}
              disabled={submitting}
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        <div className="flex-1 space-y-4 overflow-y-auto pt-1">
          {onboardingNotice &&
            (onboardingNoticeError ? (
              <Alert variant="destructive">
                <AlertDescription>{onboardingNotice}</AlertDescription>
              </Alert>
            ) : (
              <p className="text-sm text-muted-foreground">{onboardingNotice}</p>
            ))}
          {activePositions.length > 0 && (
            <div>
              <p className={labelClass}>Existing Positions</p>
              <div className="flex flex-col gap-1">
                {activePositions.map((p, idx) => (
                  <div key={`${p.funder}-${idx}`} className="flex justify-between text-sm">
                    <span>{p.funder}</span>
                    <span className="text-muted-foreground">
                      ${p.amount.toLocaleString()}/
                      {p.frequency === "daily" ? "day" : p.frequency === "weekly" ? "wk" : "mo"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activePositions.length > 0 && <Separator />}

          <div>
            <p className={labelClass}>Our Offer</p>
            <div className="grid grid-cols-2 gap-2 rounded-md border bg-muted/40 p-3 text-sm">
              <div>
                <p className="text-[0.65rem] text-muted-foreground">FUNDING</p>
                <p className="font-bold">${offerValues.fundingAmount.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-[0.65rem] text-muted-foreground">FACTOR</p>
                <p className="font-bold">{offerValues.factor}x</p>
              </div>
              <div>
                <p className="text-[0.65rem] text-muted-foreground">TERM</p>
                <p className="font-bold">{offerValues.term} days</p>
              </div>
              <div>
                <p className="text-[0.65rem] text-muted-foreground">PAYMENT</p>
                <p className="font-bold">
                  ${offerValues.installment.toLocaleString()}/{freqLabel}
                </p>
              </div>
              <div>
                <p className="text-[0.65rem] text-muted-foreground">TOTAL PAYBACK</p>
                <p className="font-bold">
                  ${Math.round(offerValues.fundingAmount * offerValues.factor).toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-[0.65rem] text-muted-foreground">ADDED LEV</p>
                <p className="font-bold">{Math.round(offerValues.addedLeverage)}%</p>
              </div>
            </div>
          </div>

          {ordinalPosition && <p className="text-sm text-muted-foreground">{ordinalPosition}</p>}

          {applicationNotes && flattenAndSort(applicationNotes).length > 0 && (
            <>
              <Separator />
              <div className="space-y-1">
                <p className={labelClass}>Existing Notes</p>
                <div className="max-h-36 space-y-2 overflow-y-auto">
                  {flattenAndSort(applicationNotes).map((note) => (
                    <div key={note.id} className="rounded border bg-muted/30 px-2 py-1.5 text-sm">
                      {note.userRole !== "system" && (
                        <span className="mr-1 text-xs font-medium">{note.userName}: </span>
                      )}
                      <span className="whitespace-pre-wrap text-xs text-muted-foreground">
                        {note.text}
                      </span>
                    </div>
                  ))}
                </div>
                {olderNotes > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {pluralize(olderNotes, "older note")} not shown
                  </p>
                )}
              </div>
            </>
          )}

          <Separator />

          <div className="space-y-2">
            <Label htmlFor="accept-notes">{proposedNote ? "Notes" : "Notes (optional)"}</Label>
            <Textarea
              id="accept-notes"
              rows={3}
              value={fixedNote ? fixedNote.text : notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={submitting}
              readOnly={!!fixedNote}
              className={cn(fixedNote && "cursor-default resize-none bg-muted/40")}
            />
            {proposedNote?.hint && (
              <p className="text-xs text-muted-foreground">{proposedNote.hint}</p>
            )}
          </div>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end sm:gap-2">
          <Button type="button" variant="outline" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          {showSendContractOption && (
            <Button
              type="button"
              variant={sendContractBlocked ? "outline" : "default"}
              className={sendContractBlocked ? undefined : "bg-green-600 hover:bg-green-600/90"}
              onClick={() => handleConfirm(true)}
              disabled={submitting || !confirmReady || sendContractBlocked}
              data-testid="approve-send-contract"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Approve + Send Contract"
              )}
            </Button>
          )}
          <Button
            type="button"
            variant={approveMain ? "default" : "outline"}
            className={approveMain ? "bg-green-600 hover:bg-green-600/90" : undefined}
            onClick={() => handleConfirm(false)}
            disabled={submitting || !confirmReady}
            data-testid="approve-only"
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              "Approve"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── DenyDialog ───────────────────────────────────────────────────────────────

interface DenyDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (reasons: DenyReason[], notes: string) => void;
  submitting: boolean;
  /** If provided, pre-populates reasons and notes from the existing assessment */
  existingAssessment?: UnderwritingAssessment | null;
  applicationNotes?: ApplicationNotesDict;
  /**
   * A decline someone else proposed (a chat decline): its reasons and note shown read-only and
   * confirmed as they are, with `hint` (how to change them) below; `editable`, they are where
   * the dialog starts and the user may change them.
   */
  proposed?: { reasons: DenyReason[]; notes: string; hint?: React.ReactNode; editable?: boolean };
  /** On a phone, sit on the bottom edge (PHONE_SHEET). */
  phoneSheet?: boolean;
}

/** Beside the Decline a proposed decline (a chat card) holds back while no reason is picked. */
export const PICK_A_REASON = "Pick at least one reason";

export const DenyDialog: React.FC<DenyDialogProps> = ({
  open,
  onClose,
  onConfirm,
  submitting,
  existingAssessment,
  applicationNotes,
  proposed,
  phoneSheet = false,
}) => {
  const initialReasons = () => proposed?.reasons ?? existingAssessment?.denyReason ?? [];
  const initialNotes = () => proposed?.notes ?? existingAssessment?.denyMoreInfo ?? "";
  const [reasons, setReasons] = useState<DenyReason[]>(initialReasons);
  const [notes, setNotes] = useState(initialNotes);
  const [reasonsOpen, setReasonsOpen] = useState(false);
  const fixed = !!proposed && !proposed.editable;

  useEffect(() => {
    if (open) {
      setReasons(initialReasons());
      setNotes(initialNotes());
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConfirm = () => {
    onConfirm(reasons, notes);
  };

  const handleClose = () => {
    if (!submitting) {
      setReasons([]);
      setNotes("");
      onClose();
    }
  };

  const half = Math.ceil(DENY_REASONS.length / 2);
  const col1 = DENY_REASONS.slice(0, half);
  const col2 = DENY_REASONS.slice(half);

  const toggleReason = (reason: DenyReason) => {
    setReasons((prev) =>
      prev.includes(reason) ? prev.filter((r) => r !== reason) : [...prev, reason]
    );
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent
        className={cn(
          "flex max-h-[85vh] max-w-lg flex-col sm:max-w-lg",
          "[&>button.absolute]:hidden",
          phoneSheet && PHONE_SHEET
        )}
        onPointerDownOutside={(e) => submitting && e.preventDefault()}
      >
        <DialogHeader className="space-y-0 text-left">
          <div className="flex items-center justify-between pr-8">
            <DialogTitle className="text-xl font-bold">Confirm Decline</DialogTitle>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              onClick={handleClose}
              disabled={submitting}
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        <div className="flex-1 space-y-4 overflow-y-auto pt-2">
          <div className="space-y-2">
            <Label>Decline Reasons</Label>
            <Popover open={reasonsOpen} onOpenChange={setReasonsOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className={cn(
                    "h-auto min-h-9 w-full justify-start text-left font-normal",
                    fixed && "disabled:opacity-100"
                  )}
                  disabled={submitting || fixed}
                >
                  {reasons.length === 0 ? (
                    <span className="text-muted-foreground">Select reasons…</span>
                  ) : (
                    <span className="flex flex-wrap gap-1">
                      {reasons.map((r) => (
                        <Badge key={r} variant="secondary" className="text-xs">
                          {r}
                        </Badge>
                      ))}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="w-[var(--radix-popover-trigger-width)] max-w-lg p-2"
                align="start"
              >
                <div className="flex gap-0 px-1 pt-1" onKeyDown={(e) => e.stopPropagation()}>
                  {[col1, col2].map((col, colIdx) => (
                    <div key={colIdx} className="min-w-0 flex-1">
                      {col.map((reason) => (
                        <label
                          key={reason}
                          className="flex cursor-pointer items-center gap-2 rounded-sm px-1 py-1.5 text-sm hover:bg-muted"
                        >
                          <Checkbox
                            checked={reasons.includes(reason)}
                            onCheckedChange={() => toggleReason(reason)}
                            disabled={submitting}
                          />
                          <span>{reason}</span>
                        </label>
                      ))}
                    </div>
                  ))}
                </div>
                <div className="flex justify-end border-t px-2 py-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setReasonsOpen(false)}
                  >
                    Done
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {applicationNotes && flattenAndSort(applicationNotes).length > 0 && (
            <div className="space-y-1">
              <p className="mb-1 text-[0.7rem] font-semibold uppercase tracking-wide text-muted-foreground">
                Existing Notes
              </p>
              <div className="max-h-36 space-y-2 overflow-y-auto">
                {flattenAndSort(applicationNotes).map((note) => (
                  <div key={note.id} className="rounded border bg-muted/30 px-2 py-1.5 text-sm">
                    {note.userRole !== "system" && (
                      <span className="mr-1 text-xs font-medium">{note.userName}: </span>
                    )}
                    <span className="whitespace-pre-wrap text-xs text-muted-foreground">
                      {note.text}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* A proposed decline keeps the box while no reason is picked, so the dialog holds still. */}
          {(reasons.length > 0 || proposed) && (
            <div className="space-y-2">
              <Label htmlFor="deny-more">Additional Information</Label>
              <Textarea
                id="deny-more"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={submitting || reasons.length === 0}
                readOnly={fixed}
                className={cn(fixed && "cursor-default resize-none bg-muted/40")}
              />
            </div>
          )}
          {proposed?.hint && <p className="text-xs text-muted-foreground">{proposed.hint}</p>}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleConfirm}
            disabled={submitting || reasons.length === 0}
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              "Decline"
            )}
          </Button>
          {/* Last, so a phone's reversed column puts it just above Decline; first from sm. */}
          {proposed && reasons.length === 0 && (
            <p className="self-center text-xs text-muted-foreground sm:order-first sm:mr-auto">
              {PICK_A_REASON}
            </p>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── ChangeDecisionDialog ─────────────────────────────────────────────────────

interface ChangeDecisionDialogProps {
  open: boolean;
  onClose: () => void;
  currentStatus: "accepted" | "declined" | "pending";
  onSelectAccept: () => void;
  onSelectDeny: () => void;
  /** When false, the Accept button is disabled (no offer entered in the calculator) */
  hasOffer: boolean;
}

export const ChangeDecisionDialog: React.FC<ChangeDecisionDialogProps> = ({
  open,
  onClose,
  currentStatus,
  onSelectAccept,
  onSelectDeny,
  hasOffer,
}) => {
  const statusLabel =
    currentStatus === "accepted"
      ? "Approved"
      : currentStatus === "declined"
        ? "Declined"
        : "Pending";

  const statusBadgeVariant =
    currentStatus === "accepted"
      ? "default"
      : currentStatus === "declined"
        ? "destructive"
        : "secondary";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className={cn("max-w-sm sm:max-w-sm", "[&>button.absolute]:hidden")}>
        <DialogHeader className="space-y-0 text-left">
          <div className="flex items-center justify-between pr-8">
            <div className="flex flex-wrap items-center gap-2">
              <DialogTitle className="text-xl font-bold">Change Decision</DialogTitle>
              <Badge variant={statusBadgeVariant}>{statusLabel}</Badge>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              onClick={onClose}
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        <div className="flex gap-3 pt-2">
          <div className="flex-1">
            <Button
              type="button"
              className="h-auto w-full flex-col gap-1 bg-green-600 py-6 hover:bg-green-600/90"
              onClick={() => {
                onClose();
                onSelectAccept();
              }}
              disabled={!hasOffer}
            >
              <Check className="h-5 w-5" />
              Approve
            </Button>
            <p
              className={cn(
                "mt-1 text-center text-xs text-muted-foreground",
                hasOffer && "invisible"
              )}
            >
              Enter an offer first
            </p>
          </div>
          <div className="flex-1">
            <Button
              type="button"
              variant="destructive"
              className="h-auto w-full flex-col gap-1 py-6"
              onClick={() => {
                onClose();
                onSelectDeny();
              }}
            >
              <Ban className="h-5 w-5" />
              Decline
            </Button>
            <p className="invisible mt-1 text-center text-xs">&nbsp;</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
